import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

const TONE_CLASS = {
  default: '',
  success: 'ads-stat-card--success',
  warning: 'ads-stat-card--warning',
  danger: 'ads-stat-card--danger',
  info: 'ads-stat-card--info',
};

const ACCENT_CLASS = {
  revenue: 'manager-hero-kpi-revenue',
  balance: 'manager-hero-kpi-net',
  costs: 'manager-hero-kpi-costs',
  inventory: 'manager-hero-kpi-inventory',
};

const formatPercent = (value, maxDecimals = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0%';
  const rounded = Math.round(n * 10 ** maxDecimals) / 10 ** maxDecimals;
  return `${rounded}%`;
};

/** Period-over-period delta chip for trend stat cards. */
export function StatCardDelta({ value, invert = false, flatLabel, className = '' }) {
  const n = Number(value) || 0;
  if (Math.abs(n) < 0.05) {
    return (
      <span className={['manager-kpi-change', 'manager-kpi-change--flat', 'ads-stat-card__delta', className]
        .filter(Boolean)
        .join(' ')}
      >
        {flatLabel}
      </span>
    );
  }
  const positive = invert ? n < 0 : n > 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={[
        'manager-kpi-change',
        positive ? 'manager-kpi-change--up' : 'manager-kpi-change--down',
        'ads-stat-card__delta',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Icon size={13} aria-hidden />
      {formatPercent(Math.abs(n), 1)}
    </span>
  );
}

/**
 * Interactive KPI card — value, optional delta, sparkline, footer.
 * Manager dashboard uses legacy manager-hero-kpi classes for density parity.
 */
export function StatCardTrend({
  label,
  value,
  icon,
  tone = 'default',
  accent,
  delta,
  sparkline,
  footer,
  footerWarn = false,
  onClick,
  ariaLabel,
  title,
  negativeValue = false,
  warn = false,
  className = '',
  as = 'button',
}) {
  const Component = as === 'button' ? 'button' : 'div';
  const interactive = Boolean(onClick);
  const accentClass = accent ? ACCENT_CLASS[accent] || '' : '';

  return (
    <Component
      type={as === 'button' ? 'button' : undefined}
      className={[
        'ads-stat-card',
        'ads-stat-card--trend',
        'manager-hero-kpi',
        TONE_CLASS[tone] || '',
        accentClass,
        negativeValue ? 'is-negative' : '',
        warn ? 'is-warn' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      role={interactive && as !== 'button' ? 'button' : undefined}
      tabIndex={interactive && as !== 'button' ? 0 : undefined}
      onKeyDown={
        interactive && as !== 'button'
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      <div className="manager-hero-kpi-head ads-stat-card__head">
        {icon ? (
          <span className="manager-hero-kpi-icon ads-stat-card__icon" aria-hidden>
            {icon}
          </span>
        ) : null}
        {label ? <span className="manager-hero-kpi-label ads-stat-card__label">{label}</span> : null}
        {interactive ? (
          <span className="manager-hero-kpi-chevron ads-stat-card__chevron" aria-hidden>
            ›
          </span>
        ) : null}
      </div>
      <div className="manager-hero-kpi-body ads-stat-card__body">
        <div className="manager-hero-kpi-main ads-stat-card__main">
          <div className="ads-stat-card__value">{value}</div>
          {delta}
        </div>
        {sparkline}
      </div>
      {footer ? (
        <div className="manager-hero-kpi-foot ads-stat-card__foot">
          <span
            className={[
              'manager-hero-kpi-foot-line',
              'ads-stat-card__foot-line',
              footerWarn ? 'manager-hero-kpi-foot-line--warn' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {footer}
          </span>
        </div>
      ) : null}
    </Component>
  );
}

export default StatCardTrend;
